/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { EcmrService } from './ecmr.service';
import { Router } from '@angular/router';
import { ExternalUserService } from '@app/core/services/ecmr-services/external-user.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
    EcmrPdfLanguageDialogComponent,
    EcmrPdfLanguage,
} from '@app/shared/components/ecmr/dialogs/ecmr-pdf-language-dialog/ecmr-pdf-language-dialog.component';

@Injectable({
    providedIn: 'root',
})
export class EcmrActionService {
    constructor(
        private ecmrService: EcmrService,
        private externalUserService: ExternalUserService,
        private router: Router,
        private spinner: NgxSpinnerService,
        private dialog: MatDialog
    ) {}

    async downloadPdf(
        ecmrId: string,
        userToken: string | null,
        tan: string | null,
        referenceId: string,
        dcaCompliant: boolean
    ) {
        // Spanish is only relevant for DeCA conform eCMRs; otherwise English is used.
        let language: EcmrPdfLanguage = 'EN';

        if (dcaCompliant) {
            const selectedLanguage = await lastValueFrom(
                this.dialog
                    .open<
                        EcmrPdfLanguageDialogComponent,
                        unknown,
                        EcmrPdfLanguage
                    >(EcmrPdfLanguageDialogComponent)
                    .afterClosed()
            );

            // The user cancelled the language dialog.
            if (!selectedLanguage) {
                return;
            }

            language = selectedLanguage;
        }

        try {
            await this.spinner.show();

            let response: HttpResponse<Blob>;
            if (userToken && tan) {
                response = await lastValueFrom(
                    this.externalUserService.downloadPdf(
                        ecmrId,
                        userToken,
                        tan,
                        language
                    )
                );
            } else {
                response = await lastValueFrom(
                    this.ecmrService.downloadPdf(ecmrId, language)
                );
            }

            this.triggerDownload(response, referenceId);
        } finally {
            this.spinner.hide();
        }
    }

    private triggerDownload(response: HttpResponse<Blob>, referenceId: string) {
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = 'ecmr-' + referenceId + '.pdf';

        if (contentDisposition) {
            const matches = /filename="([^"]*)"/.exec(contentDisposition);
            if (matches?.[1]) {
                fileName = matches[1];
            }
        }
        if (response.body) {
            const file = new Blob([response.body], {
                type: 'application/pdf',
            });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = fileName;
            link.target = '_blank';
            link.click();
        }
    }
}
